import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import DownArrowIcon from 'components/icon/down-arrow-icon';
import { gsap } from 'gsap';
import DrawSVGPlugin from 'gsap/dist/DrawSVGPlugin';
import Image from 'next/image';

export type Slide = {
  image: string;
  name: string;
  description: string;
};

type AutoplaySlidesProps = {
  title: string;
  description: string;
  linkText: string;
  linkPath: string;
  slides: readonly Slide[];
};

export default function AutoplaySlides({ title, description, linkText, linkPath, slides }: AutoplaySlidesProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const slidesLength = slides.length;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextSlide = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (currentSlide >= slidesLength - 1) {
        setCurrentSlide(0);
      } else {
        setCurrentSlide(currentSlide + 1);
      }
      setIsTransitioning(false);
    }, 300);
  }, [currentSlide, slidesLength]);

  const previousSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (currentSlide <= 0) {
        setCurrentSlide(slidesLength - 1);
      } else {
        setCurrentSlide(currentSlide - 1);
      }
      setIsTransitioning(false);
    }, 300);
  };

  const handleNextButtonClick = () => {
    stopInterval();
    nextSlide();
    startInterval();
  };

  const handlePreviousButtonClick = () => {
    stopInterval();
    previousSlide();
    startInterval();
  };

  const startInterval = useCallback(() => {
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, 5000);
  }, [nextSlide]);

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    gsap.registerPlugin(DrawSVGPlugin);
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 1 });
    tl.fromTo('#progressCirclePath', { drawSVG: 0 }, { drawSVG: '100%', duration: 6 });

    startInterval();
    return () => {
      stopInterval();
    };
  }, [startInterval]);


  return (
    <div className={styles.component}>
      <div className={styles['slide-overlay']}>
        <div className={styles['slide-overlay-title']}>
          <h2>{title}</h2>
          <p>{description}</p>
          <a href={linkPath} className={styles['button-link']}>{linkText}</a>
        </div>
        <div className={styles['arrow-button-container']}>
          <div className={styles['arrow-container']} onClick={handlePreviousButtonClick}>
            <DownArrowIcon className={buildClassName(styles['arrow'], styles['up-arrow'])} />
          </div>
          <div className={styles['arrow-container']} onClick={handleNextButtonClick}>
            <DownArrowIcon className={styles['arrow']} />
            <svg className={styles['progress-circle']}>
              <circle id='progressCirclePath' className={styles['progress-circle-path']} cx="80" cy="20" r="20" stroke="#ffffff" strokeWidth="2" fill="transparent" transform="rotate(-90, 50, 50)" />
            </svg>
          </div>
        </div>
      </div>
      <div className={styles['slide-container']}>
        {slides.map((slide, index) => (
          <div key={index} className={buildClassName(styles.slide, index === currentSlide && styles['slide-active'], isTransitioning && styles['slide-transitioning'])}>
            <div className={styles['slide-content']}>
              <h3>{slide.name}</h3>
              <p>{slide.description}</p>
            </div>
            <Image src={slide.image} alt={slide.name} fill sizes='100vw' quality={100} style={{ objectFit: 'cover' }} />
          </div>
        ))}
      </div>
    </div>
  );
}
